import { FormattedMessage, useIntl, type IntlShape } from 'react-intl';
import { Grid, Row, Col } from 'react-flexbox-grid';
import { useParams } from 'react-router-dom';
import PageTitle from 'components/common/PageTitle';
import LoadingOverlay from 'components/common/LoadingOverlay';
import useProjectPlan from 'hooks/project-plan/useProjectPlan';
import { routePatterns } from 'router/route-paths';
import Alert from 'components/common/Alert';
import WarningMessage from 'components/common/WarningMessage';
import ProjectPlanTables from 'components/plan/ProjectPlanTables';
import ActionButtons from 'components/plan/ActionButtons';
import { PROPERTY_VALUATION } from 'constants/constants';
import StartProjectPlanning from 'components/calculators/PropertyValuation/StartProjectPlanning';
import styles from './Plan.module.scss';

interface IPlanProps {
  projectId: string;
  t: IntlShape['formatMessage'];
}

function Plan({ projectId, t }: IPlanProps) {
  const [{ projectPlan, project }, loading] = useProjectPlan(projectId);

  if (loading) {
    return <LoadingOverlay />;
  }

  if (!project) {
    return <Alert color="danger">{t({ id: 'app.project_not_found' })}</Alert>;
  }

  if (project.type === PROPERTY_VALUATION) {
    return <StartProjectPlanning />;
  }

  if (!projectPlan?.planningCategories) {
    return (
      <Alert color="danger" withContainer>
        {t({ id: 'app.project_not_found' })}
      </Alert>
    );
  }

  if (projectPlan?.price === 0) {
    return (
      <Row center="xs">
        <Col md={8} lg={6}>
          <WarningMessage
            title={t({ id: 'page_titles.plan' })}
            message={t({ id: 'project_plan.complete_previous_step' })}
            btnLabel={t({ id: 'project_plan.complete_previous_step_btn' })}
            btnLink={routePatterns.projectDetailsCalculator.stringify({ id: projectId })}
          />
        </Col>
      </Row>
    );
  }

  return (
    <Grid className={styles.container}>
      <Row>
        <Col xs={12}>
          <div className={styles.header}>
            <PageTitle>
              <FormattedMessage id="page_titles.plan" />
            </PageTitle>
            <ActionButtons projectId={project._id} pdf={projectPlan.pdf} />
          </div>
        </Col>
      </Row>
      <ProjectPlanTables project={project} projectPlan={projectPlan} />
    </Grid>
  );
}

export default () => {
  const { id: projectId } = useParams<{ id?: string }>();
  const { formatMessage: t } = useIntl();

  if (!projectId) {
    return (
      <Alert color="danger" withContainer>
        {t({ id: 'app.project_not_found' })}
      </Alert>
    );
  }

  return <Plan {...{ projectId, t }} />;
};
